/**
 * Pure helpers for the homepage "Financial-first findings" counters.
 *
 * The DOM markup renders each stat as `<span class="counter">90</span>B+`, where the
 * counter span holds only the numeric portion (e.g. `90`, `2.7`, `1,500`). These helpers
 * parse that target and format intermediate values so the animation can roll up from zero.
 */

export interface ParsedCounter {
  target: number
  /** Number of decimal places to preserve (0 for integers like `90`, 1 for `2.7`). */
  decimals: number
  /** Whether the original text used thousands separators (e.g. `1,500`). */
  hasThousandsSeparator: boolean
}

/**
 * Parse the numeric target out of a counter's text content.
 * Returns null when the text is not a plain number (nothing to animate).
 */
export function parseCounter(rawText: string): ParsedCounter | null {
  const trimmed = rawText.trim()
  const hasThousandsSeparator = /,/.test(trimmed)
  // If commas are present they must form valid thousands groups (e.g. `1,500`, `1,234,567`);
  // reject malformed grouping like `2,7` before stripping the separators.
  if (hasThousandsSeparator && !/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(trimmed)) return null
  const cleaned = trimmed.replace(/,/g, '')

  if (!/^\d+(\.\d+)?$/.test(cleaned)) return null

  const target = parseFloat(cleaned)
  const decimals = cleaned.includes('.') ? (cleaned.split('.')[1] ?? '').length : 0

  return { target, decimals, hasThousandsSeparator }
}

/** Format an intermediate animation value to match the target's shape. */
export function formatCounter(value: number, parsed: ParsedCounter): string {
  let formatted = value.toFixed(parsed.decimals)
  if (parsed.hasThousandsSeparator) {
    formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  return formatted
}
